import olFeature from 'ol/Feature';
import { asArray as ColorAsArray } from 'ol/color';
import type { default as OlGeometry } from 'ol/geom/Geometry';
import * as olStyle from 'ol/style';

import { Feature } from '../../../feature/shared/feature.interfaces';
import { createOverlayMarkerStyle } from '../overlay/overlay-marker-style.utils';
import { createOverlayDefaultStyle } from '../overlay/overlay-style.utils';
import { FeatureCommonVectorStyleOptions } from './vector-style.interface';

/**
 * Generate a style for selected features
 * @param feature The feature to generate the style
 * @returns A olStyle
 */
export function getCommonVectorSelectedStyle({
  feature,
  markerColor = [0, 161, 222],
  markerOpacity = 1,
  markerOutlineColor = [0, 255, 255],
  fillColor,
  fillOpacity = 0.15,
  strokeColor = [0, 255, 255],
  strokeOpacity = 0.5,
  strokeWidth = 4
}: FeatureCommonVectorStyleOptions): olStyle.Style {
  return getCommonVectorStyle({
    feature,
    markerColor,
    markerOpacity,
    markerOutlineColor,
    fillColor,
    fillOpacity,
    strokeColor,
    strokeOpacity,
    strokeWidth
  });
}

/**
 * Generate a basic style for features
 * @param feature The feature to generate the style
 * @returns A olStyle
 */
export function getCommonVectorStyle({
  feature,
  markerColor = [0, 161, 222],
  markerOpacity = 0.5,
  markerOutlineColor,
  fillColor = [0, 161, 222],
  fillOpacity = 0.15,
  strokeColor = [0, 161, 222],
  strokeOpacity = 0.5,
  strokeWidth = 2
}: FeatureCommonVectorStyleOptions): olStyle.Style {
  const isOlFeature = feature instanceof olFeature;
  let geometry;
  let text;
  if (isOlFeature) {
    feature = feature as olFeature<OlGeometry>;
    geometry = feature.getGeometry();
  } else {
    feature = feature as Feature;
    geometry = feature.geometry;
    text = feature.meta.mapTitle;
  }
  const geometryType = isOlFeature ? geometry.getType() : geometry.type;

  if (!geometry || geometryType === 'Point') {
    const markerColorAsArray = ColorAsArray(markerColor).slice(0);
    const markerColorRGB = markerColorAsArray.slice(0, 3);

    if (
      markerColorAsArray.length === 4 &&
      (typeof markerColor !== 'string' ||
        /^#[0-9A-F]{8}$/i.test(markerColor as string))
    ) {
      markerOpacity = markerColorAsArray[3];
    }

    return createOverlayMarkerStyle({
      text,
      opacity: markerOpacity,
      markerOutlineColor,
      markerColor: markerColorRGB
    });
  } else {
    const fillWithOpacity = ColorAsArray(fillColor).slice(0);
    const strokeWithOpacity = ColorAsArray(strokeColor).slice(0);

    if (!(
      fillWithOpacity.length === 4 &&
      (typeof fillColor !== 'string' ||
        /^#[0-9A-F]{8}$/i.test(fillColor as string))
    )) {
      fillWithOpacity[3] = fillOpacity;
    }

    if (!(
      strokeWithOpacity.length === 4 &&
      (typeof strokeColor !== 'string' ||
        /^#[0-9A-F]{8}$/i.test(strokeColor as string))
    )) {
      strokeWithOpacity[3] = strokeOpacity;
    }

    return createOverlayDefaultStyle({
      text,
      strokeWidth,
      strokeColor: strokeWithOpacity,
      fillColor: fillWithOpacity
    });
  }
}
